import axios, { AxiosRequestConfig } from "axios";
import { apiTimeout } from "../../Utils/Constants/apiConstants";
import {
  encryptEnvelope,
  importServerKey,
  parsePublicKeyInfo,
  ServerKey,
  WebEncryptionError,
} from "./envelope";
import { isWebEncryptedRoute, PUBLIC_KEY_ROUTE } from "./routes";

export { WebEncryptionError } from "./envelope";
export { WEB_ROUTES, isWebRoute, isWebEncryptedRoute } from "./routes";

/** Backend error codes after which the key is refetched and the request re-encrypted once. */
export const KEY_REFRESH_ERROR_CODES = ["WEB_ENC_UNKNOWN_KEY", "WEB_ENC_STALE"];

// The server key is cached in memory only, never in browser storage.
let cachedKey: ServerKey | null = null;
let keyRequest: Promise<ServerKey> | null = null;

async function fetchServerKey(): Promise<ServerKey> {
  let body: unknown;
  try {
    // Separate instance: no app interceptors, no Authorization header, no cookies.
    const response = await axios.create().get(PUBLIC_KEY_ROUTE, {
      baseURL: axios.defaults.baseURL,
      headers: { Accept: "application/json" },
      withCredentials: false,
      timeout: apiTimeout,
    });
    body = response.data;
  } catch {
    throw new WebEncryptionError("KEY_UNAVAILABLE");
  }
  return importServerKey(parsePublicKeyInfo(body));
}

/** Returns the cached server key; `force` refetches it (key rotation, clock correction). */
export function loadPublicKey(force = false): Promise<ServerKey> {
  if (cachedKey && !force) {
    return Promise.resolve(cachedKey);
  }
  if (!keyRequest) {
    keyRequest = fetchServerKey()
      .then((key) => {
        cachedKey = key;
        return key;
      })
      .finally(() => {
        keyRequest = null;
      });
  }
  return keyRequest;
}

export function clearPublicKeyCache() {
  cachedKey = null;
}

/**
 * Opaque handle to a request's plaintext body. The plaintext lives in a WeakMap rather than on
 * the request config, so it is not exposed through config/error objects (which may be logged),
 * yet a retry can still be re-encrypted with a fresh key and nonce.
 */
class PlaintextBody {}
const plaintexts = new WeakMap<PlaintextBody, unknown>();

type EncryptableConfig = AxiosRequestConfig & { _webEncBody?: PlaintextBody };

function fail(error: unknown, config: AxiosRequestConfig): never {
  const failure =
    error instanceof WebEncryptionError ? error : new WebEncryptionError("ENCRYPTION_FAILED");
  throw Object.assign(failure, { config });
}

/**
 * Replaces the request body with an encrypted envelope. Throws instead of sending anything if
 * the route is not on the allowlist or encryption is not possible: there is no plaintext fallback.
 */
export async function encryptRequest(config: EncryptableConfig): Promise<AxiosRequestConfig> {
  const method = (config.method || "get").toUpperCase();
  const route = config.url || "";
  if (!isWebEncryptedRoute(method, route)) {
    fail(new WebEncryptionError("ROUTE_NOT_ALLOWED"), config);
  }
  if (!(config._webEncBody instanceof PlaintextBody)) {
    const handle = new PlaintextBody();
    plaintexts.set(handle, config.data);
    config._webEncBody = handle;
  }
  try {
    const key = await loadPublicKey();
    config.data = await encryptEnvelope(key, method, route, plaintexts.get(config._webEncBody));
  } catch (error) {
    config.data = undefined;
    fail(error, config);
  }
  config.headers = {
    ...config.headers,
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  return config;
}

export function isEncryptedRequest(config: unknown) {
  return !!config && (config as EncryptableConfig)._webEncBody instanceof PlaintextBody;
}
